/**
 * Classi di Concorso - Motore di ricerca contestuale
 * Ricerca unificata: accetta sia classi di concorso che titoli di studio.
 * I risultati sono cliccabili per navigazione bidirezionale.
 */

(function () {
  'use strict';

  const ORD_LABELS = {
    VO: 'Vecchio Ordinamento (DM 39/1998)',
    LM: 'Lauree Magistrali (DM 270/2004)',
    LS: 'Lauree Specialistiche (DM 22/2005)',
  };

  let DB = null;
  let currentView = null; // { type, key } or null (search results)
  let scrollObserver = null;

  // DOM refs
  const section = document.getElementById('cdc-app');
  if (!section) return;

  const input = section.querySelector('.cdc-search-input');
  const clearBtn = section.querySelector('.cdc-search-clear');
  const acList = section.querySelector('.cdc-autocomplete');
  const resultsEl = section.querySelector('.cdc-results');
  const statusEl = section.querySelector('.cdc-status');
  const stickyNav = section.querySelector('.cdc-nav-sticky');
  let acIndex = -1;

  // Load database
  async function loadDB() {
    if (DB) return DB;
    statusEl.className = 'cdc-status cdc-status--loading';
    statusEl.textContent = 'Caricamento database...';
    try {
      const url = section.dataset.dbUrl;
      const resp = await fetch(url);
      DB = await resp.json();
      statusEl.className = 'cdc-status cdc-status--initial';
      statusEl.innerHTML = 'Cerca una <strong>classe di concorso</strong> (es. A-12, B-03) oppure un <strong>titolo di studio</strong> (es. Laurea in Filosofia)';
      return DB;
    } catch (e) {
      statusEl.className = 'cdc-status';
      statusEl.textContent = 'Errore nel caricamento dei dati.';
      console.error('CDC DB load error:', e);
      return null;
    }
  }

  // Normalize for search
  function norm(s) {
    return s.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Search index
  function search(query, limit) {
    if (!DB) return [];
    const q = norm(query);
    if (q.length < 2) return [];

    const qParts = q.split(' ').filter(Boolean);
    const results = [];

    for (const item of DB.ix) {
      const s = norm(item.s);
      let score = 0;

      // Exact code match (highest priority)
      const codeLower = (item.k || '').toLowerCase().replace('-', '');
      const qClean = q.replace(/[\s-]/g, '');
      if (codeLower.replace('-', '') === qClean) {
        score = 1000;
      } else if (s.startsWith(q)) {
        score = 100;
      } else {
        // All parts must match
        let allMatch = true;
        for (const part of qParts) {
          if (!s.includes(part)) { allMatch = false; break; }
        }
        if (allMatch) {
          score = 10;
          // Bonus for earlier match position
          const pos = s.indexOf(qParts[0]);
          if (pos >= 0) score += Math.max(0, 50 - pos);
        }
      }

      if (score > 0) {
        results.push({ ...item, score });
      }
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, limit || 10);
  }

  // Debounce
  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // Escape HTML
  function esc(s) {
    const d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  // Highlight match in text
  function highlight(text, query) {
    if (!query) return esc(text);
    const escaped = esc(text);
    const q = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return escaped.replace(new RegExp('(' + q + ')', 'gi'), '<strong>$1</strong>');
  }

  // Render autocomplete
  function renderAC(results, query) {
    if (!results.length) {
      acList.classList.remove('open');
      return;
    }

    acList.innerHTML = results.map((r, i) => {
      const badgeClass = r.y === 'c' ? 'classe' : 'titolo';
      const badgeText = r.y === 'c' ? 'Classe' : 'Titolo';
      return `<div class="cdc-ac-item" data-index="${i}" data-type="${r.y}" data-key="${esc(r.k)}">
        <span class="cdc-ac-badge cdc-ac-badge--${badgeClass}">${badgeText}</span>
        <span class="cdc-ac-text">${highlight(r.s, query)}</span>
      </div>`;
    }).join('');

    acIndex = -1;
    acList.classList.add('open');
  }

  function closeAC() {
    acList.classList.remove('open');
    acIndex = -1;
  }

  // Navigate to a specific classe or titolo
  function navigateTo(type, key) {
    closeAC();
    currentView = { type, key };

    if (type === 'c' || type === 'classe') {
      renderClasse(key);
    } else {
      renderTitolo(key);
    }
  }

  // Render single classe di concorso
  function renderClasse(codice) {
    const c = DB.c[codice];
    if (!c) {
      resultsEl.innerHTML = '<div class="cdc-status">Classe non trovata.</div>';
      resultsEl.classList.add('visible');
      statusEl.style.display = 'none';
      return;
    }

    let html = '';

    // Back button
    html += `<button class="cdc-back" onclick="window.cdcBack()">&#8592; Torna ai risultati</button>`;

    // Header
    html += `<div class="cdc-result-header">
      <span class="cdc-result-code">${esc(codice)}</span>
      <span class="cdc-result-name">${esc(c.d)}</span>
    </div>`;
    html += `<div class="cdc-result-type-badge cdc-result-type-badge--classe">Classe di concorso</div>`;

    // Titoli di accesso per ordinamento
    const ordKeys = Object.keys(c.ta);
    const navItems = [];

    if (ordKeys.length) {
      for (const ok of ordKeys) {
        const label = ORD_LABELS[ok] || ok;
        const sectionId = 'cdc-s-' + ok.toLowerCase().replace(/[^a-z0-9]/g, '');
        navItems.push({ id: sectionId, label: label });

        html += `<div class="cdc-block" id="${sectionId}">
          <div class="cdc-block-title">${esc(label)}</div>`;
        html += `<div class="cdc-table-wrap"><table class="cdc-table">
          <thead><tr><th>Titolo di studio</th><th>Note</th></tr></thead><tbody>`;

        for (const t of c.ta[ok]) {
          const noteHtml = renderNotes(t.nt);
          html += `<tr data-type="t" data-key="${esc(t.n)}" onclick="window.cdcNav('t','${esc(t.n).replace(/'/g, "\\'")}')">
            <td class="cdc-link-cell">${esc(t.n)}</td>
            <td class="cdc-col-note-cell">${noteHtml}</td>
          </tr>`;
        }
        html += `</tbody></table></div></div>`;
      }
    }

    // Dove insegnare
    if (c.is && c.is.length) {
      const istId = 'cdc-s-istituti';
      navItems.push({ id: istId, label: 'Dove insegnare' });

      html += `<div class="cdc-block" id="${istId}">
        <div class="cdc-block-title">Dove puoi insegnare</div>
        <div class="cdc-table-wrap"><table class="cdc-table">
          <thead><tr><th>Istituto</th><th>Materia</th><th>Nota</th></tr></thead><tbody>`;
      for (const ist of c.is) {
        html += `<tr>
          <td>${esc(ist.i)}</td>
          <td>${esc(ist.m)}</td>
          <td>${ist.nt ? esc(ist.nt) : ''}</td>
        </tr>`;
      }
      html += `</tbody></table></div></div>`;
    }

    resultsEl.innerHTML = html;
    resultsEl.classList.add('visible');
    statusEl.style.display = 'none';
    updateStickyNav(navItems);
  }

  // Render single titolo di studio
  function renderTitolo(nome) {
    const t = DB.t[nome];
    if (!t) {
      resultsEl.innerHTML = '<div class="cdc-status">Titolo non trovato.</div>';
      resultsEl.classList.add('visible');
      statusEl.style.display = 'none';
      return;
    }

    let html = '';

    html += `<button class="cdc-back" onclick="window.cdcBack()">&#8592; Torna ai risultati</button>`;

    html += `<div class="cdc-result-header">
      <span class="cdc-result-name" style="font-size:2.2rem;font-weight:700;">${esc(nome)}</span>
    </div>`;
    html += `<div class="cdc-result-type-badge cdc-result-type-badge--titolo">${esc(t.l || 'Titolo di studio')}</div>`;

    // Classi di concorso accessibili
    const navItems = [];
    if (t.cl && t.cl.length) {
      const clId = 'cdc-s-classi';
      navItems.push({ id: clId, label: 'Classi accessibili (' + t.cl.length + ')' });

      html += `<div class="cdc-block" id="${clId}">
        <div class="cdc-block-title">Classi di concorso accessibili (${t.cl.length})</div>
        <div class="cdc-table-wrap"><table class="cdc-table">
          <thead><tr><th>Codice</th><th>Classe di concorso</th><th>Ordinamento</th><th>Note</th></tr></thead><tbody>`;

      for (const cl of t.cl) {
        const ordLabel = ORD_LABELS[cl.o] || cl.o;
        const noteHtml = renderNotes(cl.nt);
        html += `<tr data-type="c" data-key="${esc(cl.c)}" onclick="window.cdcNav('c','${esc(cl.c).replace(/'/g, "\\'")}')">
          <td class="cdc-col-code cdc-link-cell">${esc(cl.c)}</td>
          <td class="cdc-link-cell">${esc(cl.d)}</td>
          <td class="cdc-col-ord">${esc(ordLabel)}</td>
          <td class="cdc-col-note-cell">${noteHtml}</td>
        </tr>`;
      }
      html += `</tbody></table></div></div>`;
    }

    resultsEl.innerHTML = html;
    resultsEl.classList.add('visible');
    statusEl.style.display = 'none';
    updateStickyNav(navItems);
  }

  // Render notes
  function renderNotes(notes) {
    if (!notes || !notes.length) return '';
    return '<ul class="cdc-note-list">' +
      notes.map(n => `<li class="cdc-note-item"><span class="cdc-note-label">${esc(n.t)}:</span> ${esc(n.x)}</li>`).join('') +
      '</ul>';
  }

  // Sticky navigation
  function updateStickyNav(items) {
    // Cleanup previous observer
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }

    if (!items || items.length < 2) {
      stickyNav.classList.remove('visible');
      stickyNav.innerHTML = '';
      return;
    }

    // Filterable items = ordinamenti (not "istituti")
    const filterableIds = items.filter(i => i.id !== 'cdc-s-istituti').map(i => i.id);
    const hasFilters = filterableIds.length > 1;

    let navHtml = '';
    if (hasFilters) {
      navHtml += `<a class="cdc-nav-item cdc-nav-filter active" data-filter="all">Tutti</a>`;
    }
    navHtml += items.map(item => {
      const isFilterable = filterableIds.includes(item.id);
      const filterAttr = isFilterable ? ` data-filter="${item.id}"` : '';
      const cls = isFilterable ? 'cdc-nav-item cdc-nav-filter' : 'cdc-nav-item';
      return `<a class="${cls}" href="#${item.id}" data-target="${item.id}"${filterAttr}>${esc(item.label)}</a>`;
    }).join('');

    stickyNav.innerHTML = navHtml;
    stickyNav.classList.add('visible');

    // Click handler
    stickyNav.querySelectorAll('.cdc-nav-item').forEach(link => {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        const filterId = this.dataset.filter;

        // Filter mode: show/hide ordinamento sections
        if (filterId !== undefined) {
          applyFilter(filterId, filterableIds, items);
          return;
        }

        // Scroll mode (non-filterable items like "dove insegnare")
        const target = document.getElementById(this.dataset.target);
        if (target) {
          const navH = stickyNav.offsetHeight || 50;
          const top = target.getBoundingClientRect().top + window.scrollY - navH - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    });

    // IntersectionObserver for active state tracking
    const targets = items.map(i => document.getElementById(i.id)).filter(Boolean);
    if (!targets.length) return;

    scrollObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          stickyNav.querySelectorAll('.cdc-nav-item').forEach(el => {
            el.classList.toggle('active', el.dataset.target === entry.target.id);
          });
        }
      }
    }, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0
    });

    targets.forEach(t => scrollObserver.observe(t));
  }

  function applyFilter(filterId, filterableIds, allItems) {
    const showAll = filterId === 'all';

    // Toggle visibility of ordinamento blocks
    filterableIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.style.display = (showAll || id === filterId) ? '' : 'none';
      }
    });

    // Update active state on filter buttons
    stickyNav.querySelectorAll('.cdc-nav-filter').forEach(btn => {
      if (showAll) {
        btn.classList.toggle('active', btn.dataset.filter === 'all');
      } else {
        btn.classList.toggle('active', btn.dataset.filter === filterId);
      }
    });

    // Scroll to the filtered section if single filter
    if (!showAll) {
      const target = document.getElementById(filterId);
      if (target) {
        const navH = stickyNav.offsetHeight || 50;
        const top = target.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }

  function hideStickyNav() {
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }
    stickyNav.classList.remove('visible');
    stickyNav.innerHTML = '';
  }

  // Render search results list (multiple matches)
  let lastSearchResults = [];

  function renderSearchResults(results, query) {
    if (!results.length) {
      resultsEl.classList.remove('visible');
      statusEl.style.display = '';
      statusEl.className = 'cdc-status';
      statusEl.textContent = `Nessun risultato per "${query}"`;
      return;
    }

    currentView = null;
    lastSearchResults = results;
    hideStickyNav();

    let html = `<div class="cdc-count">${results.length} risultat${results.length === 1 ? 'o' : 'i'} per "${esc(query)}"</div>`;
    html += `<div class="cdc-table-wrap"><table class="cdc-table">
      <thead><tr><th style="width:8rem">Tipo</th><th>Risultato</th></tr></thead><tbody>`;

    for (const r of results) {
      const badgeClass = r.y === 'c' ? 'classe' : 'titolo';
      const badgeText = r.y === 'c' ? 'Classe' : 'Titolo';
      html += `<tr data-type="${r.y}" data-key="${esc(r.k)}" onclick="window.cdcNav('${r.y}','${esc(r.k).replace(/'/g, "\\'")}')">
        <td><span class="cdc-ac-badge cdc-ac-badge--${badgeClass}">${badgeText}</span></td>
        <td class="cdc-link-cell">${highlight(r.s, query)}</td>
      </tr>`;
    }
    html += `</tbody></table></div>`;

    resultsEl.innerHTML = html;
    resultsEl.classList.add('visible');
    statusEl.style.display = 'none';
  }

  // Global navigation functions
  window.cdcNav = function (type, key) {
    navigateTo(type, key);
    window.scrollTo({ top: section.offsetTop - 80, behavior: 'smooth' });
  };

  window.cdcBack = function () {
    hideStickyNav();
    if (lastSearchResults.length && input.value.trim()) {
      renderSearchResults(lastSearchResults, input.value.trim());
    } else {
      resultsEl.classList.remove('visible');
      statusEl.style.display = '';
      statusEl.className = 'cdc-status cdc-status--initial';
      statusEl.innerHTML = 'Cerca una <strong>classe di concorso</strong> (es. A-12, B-03) oppure un <strong>titolo di studio</strong> (es. Laurea in Filosofia)';
    }
    currentView = null;
    input.focus();
  };

  // Input handler
  const onInput = debounce(async function () {
    const q = input.value.trim();
    clearBtn.classList.toggle('visible', q.length > 0);

    if (q.length < 2) {
      closeAC();
      if (!currentView) {
        resultsEl.classList.remove('visible');
        statusEl.style.display = '';
        statusEl.className = 'cdc-status cdc-status--initial';
        statusEl.innerHTML = 'Cerca una <strong>classe di concorso</strong> (es. A-12, B-03) oppure un <strong>titolo di studio</strong> (es. Laurea in Filosofia)';
      }
      return;
    }

    await loadDB();
    if (!DB) return;

    const results = search(q, 10);
    renderAC(results, q);
  }, 200);

  // Submit search (Enter or all results)
  function submitSearch() {
    closeAC();
    const q = input.value.trim();
    if (q.length < 2 || !DB) return;

    const results = search(q, 50);

    // If single exact match, navigate directly
    if (results.length === 1 && results[0].score >= 100) {
      navigateTo(results[0].y, results[0].k);
    } else {
      renderSearchResults(results, q);
    }
  }

  // Event listeners
  input.addEventListener('input', onInput);
  input.addEventListener('focus', async function () {
    await loadDB();
    if (input.value.trim().length >= 2) {
      onInput();
    }
  });

  input.addEventListener('keydown', function (e) {
    const items = acList.querySelectorAll('.cdc-ac-item');
    if (!items.length) {
      if (e.key === 'Enter') {
        e.preventDefault();
        submitSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      acIndex = Math.min(acIndex + 1, items.length - 1);
      items.forEach((el, i) => el.classList.toggle('active', i === acIndex));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      acIndex = Math.max(acIndex - 1, -1);
      items.forEach((el, i) => el.classList.toggle('active', i === acIndex));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (acIndex >= 0 && items[acIndex]) {
        const item = items[acIndex];
        navigateTo(item.dataset.type, item.dataset.key);
      } else {
        submitSearch();
      }
    } else if (e.key === 'Escape') {
      closeAC();
    }
  });

  clearBtn.addEventListener('click', function () {
    input.value = '';
    clearBtn.classList.remove('visible');
    closeAC();
    hideStickyNav();
    resultsEl.classList.remove('visible');
    statusEl.style.display = '';
    statusEl.className = 'cdc-status cdc-status--initial';
    statusEl.innerHTML = 'Cerca una <strong>classe di concorso</strong> (es. A-12, B-03) oppure un <strong>titolo di studio</strong> (es. Laurea in Filosofia)';
    currentView = null;
    lastSearchResults = [];
    input.focus();
  });

  acList.addEventListener('click', function (e) {
    const item = e.target.closest('.cdc-ac-item');
    if (item) {
      navigateTo(item.dataset.type, item.dataset.key);
    }
  });

  // Close autocomplete on outside click
  document.addEventListener('click', function (e) {
    if (!section.contains(e.target)) {
      closeAC();
    }
  });

  // Initial DB load
  loadDB();
})();
